/**
 * Settings Data Transformers
 *
 * Transforms data between old frontend format and new backend API format
 *
 * Old Format (Frontend):
 * {
 *   payment: { currency: "GBP", tax_rate_uk: 0.20 },
 *   general: { storeName: "My Store" }
 * }
 *
 * New Format (Backend API):
 * {
 *   payment: [
 *     { id: "...", section: "payment", key: "currency", value: "GBP", ... },
 *     { id: "...", section: "payment", key: "tax_rate_uk", value: "0.20", ... }
 *   ]
 * }
 */

/**
 * Transform new API grouped response to old frontend format
 * @param {Object} groupedSettings - Settings grouped by section from API
 * @returns {Object} Settings in old frontend format
 *
 * @example
 * const apiResponse = {
 *   payment: [
 *     { section: "payment", key: "currency", value: "GBP" },
 *     { section: "payment", key: "tax_rate_uk", value: "0.20" }
 *   ]
 * };
 * const oldFormat = transformNewToOld(apiResponse);
 * // Result: { general: { currency: "GBP" }, tax: { taxRateUk: 0.20 }, shipping: {...} }
 */
export const transformNewToOld = (groupedSettings) => {
  if (!groupedSettings || typeof groupedSettings !== 'object') {
    console.warn('[Transform] Invalid input to transformNewToOld:', groupedSettings);
    return {};
  }

  // Initialize with default structure (backend data will replace these defaults)
  const result = {
    general: {
      currency: 'GBP' // Default currency (replaced by backend data if available)
    },
    shipping: {
      shippingCosts: {
        uk: 0,  // Default (replaced by backend data if available)
        eu: 0,  // Default (replaced by backend data if available)
        us: 0   // Default (replaced by backend data if available)
      }
    },
    tax: {
      taxRates: {
        uk: 0,  // Default (replaced by backend data if available)
        eu: 0,  // Default (replaced by backend data if available)
        us: 0   // Default (replaced by backend data if available)
      }
    }
  };

  for (const [section, settings] of Object.entries(groupedSettings)) {
    if (!Array.isArray(settings)) {
      console.warn(`[Transform] Section "${section}" is not an array, skipping`);
      continue;
    }

    // QUICK WIN: Map payment section to appropriate UI sections
    if (section === 'payment') {
      for (const setting of settings) {
        if (!setting || !setting.key) {
          console.warn('[Transform] Invalid setting object:', setting);
          continue;
        }

        const { key, value } = setting;
        const transformedValue = parseSettingValue(key, value);

        // Map payment keys to UI sections (REPLACES defaults with real backend data)
        if (key === 'currency') {
          result.general.currency = transformedValue;
        } else if (key.startsWith('tax_rate_')) {
          const region = key.replace('tax_rate_', '');
          result.tax.taxRates[region] = transformedValue;
        } else if (key.startsWith('shipping_cost_')) {
          const region = key.replace('shipping_cost_', '');
          result.shipping.shippingCosts[region] = transformedValue;
        } else {
          // Unknown payment key, store it anyway
          if (!result.payment) result.payment = {};
          result.payment[key] = transformedValue;
        }
      }
    } else {
      // Handle other sections normally
      if (!result[section]) result[section] = {};

      for (const setting of settings) {
        if (!setting || !setting.key) {
          console.warn('[Transform] Invalid setting object:', setting);
          continue;
        }

        const { key, value } = setting;
        const transformedValue = parseSettingValue(key, value);

        // Handle nested paths (e.g., "store_address_line1" → address.line1)
        if (key.includes('_') && shouldNestKey(key)) {
          setNestedValue(result[section], key, transformedValue);
        } else {
          result[section][key] = transformedValue;
        }
      }
    }
  }

  return result;
};

/**
 * Transform old frontend format to new API format
 * @param {Object} settingsObject - Settings in old frontend format
 * @returns {Array} Array of settings for API batch update
 *
 * @example
 * const oldFormat = {
 *   general: { currency: "GBP" },
 *   tax: { taxRates: { uk: 0.20 } }
 * };
 * const apiFormat = transformOldToNew(oldFormat);
 * // Result: [
 * //   { section: 'payment', key: 'currency', value: 'GBP' },
 * //   { section: 'payment', key: 'tax_rate_uk', value: '0.20' }
 * // ]
 */
export const transformOldToNew = (settingsObject) => {
  if (!settingsObject || typeof settingsObject !== 'object') {
    console.warn('[Transform] Invalid input to transformOldToNew:', settingsObject);
    return [];
  }

  const result = [];

  // QUICK WIN: Map UI sections back to payment section
  for (const [section, fields] of Object.entries(settingsObject)) {
    if (!fields || typeof fields !== 'object') {
      console.warn(`[Transform] Invalid fields for section "${section}":`, fields);
      continue;
    }

    if (section === 'general') {
      // Map general.currency → payment.currency
      if (fields.currency !== undefined) {
        result.push({
          section: 'payment',
          key: 'currency',
          value: stringifySettingValue('currency', fields.currency)
        });
      }
    } else if (section === 'tax') {
      // Map tax.taxRates.{region} → payment.tax_rate_{region}
      if (fields.taxRates && typeof fields.taxRates === 'object') {
        for (const [region, value] of Object.entries(fields.taxRates)) {
          const key = `tax_rate_${region}`;
          result.push({
            section: 'payment',
            key: key,
            value: stringifySettingValue(key, value)
          });
        }
      }
    } else if (section === 'shipping') {
      // Map shipping.shippingCosts.{region} → payment.shipping_cost_{region}
      if (fields.shippingCosts && typeof fields.shippingCosts === 'object') {
        for (const [region, value] of Object.entries(fields.shippingCosts)) {
          const key = `shipping_cost_${region}`;
          result.push({
            section: 'payment',
            key: key,
            value: stringifySettingValue(key, value)
          });
        }
      }
    } else if (section === 'payment') {
      // Direct payment section (if any)
      for (const [key, value] of Object.entries(fields)) {
        result.push({
          section: 'payment',
          key: key,
          value: stringifySettingValue(key, value)
        });
      }
    } else {
      // Other sections - keep as-is for future expansion
      for (const [key, value] of Object.entries(fields)) {
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
          // Flatten nested object
          const flattened = flattenObject(value, key);
          for (const flatItem of flattened) {
            result.push({
              section: section,
              key: flatItem.key,
              value: stringifySettingValue(flatItem.key, flatItem.value)
            });
          }
        } else {
          result.push({
            section: section,
            key: key,
            value: stringifySettingValue(key, value)
          });
        }
      }
    }
  }

  return result;
};

/**
 * Parse string value from API to appropriate JavaScript type
 * @param {string} key - Setting key (used to infer type)
 * @param {string} stringValue - Value from API (always string)
 * @returns {string|number|boolean} Parsed value
 */
const parseSettingValue = (key, stringValue) => {
  if (stringValue === null || stringValue === undefined) {
    return stringValue;
  }

  // Convert to string if not already
  const strValue = String(stringValue);

  // Boolean values
  if (strValue === 'true') return true;
  if (strValue === 'false') return false;

  // Number values (rates, costs, thresholds, counts)
  const numberPatterns = [
    'rate', 'cost', 'threshold', 'price', 'amount',
    'shipping', 'tax', 'count', 'limit', 'min', 'max'
  ];

  const isNumber = numberPatterns.some(pattern =>
    key.toLowerCase().includes(pattern)
  );

  if (isNumber) {
    const num = parseFloat(strValue);
    if (!isNaN(num)) return num;
  }

  // Default: return as string
  return strValue;
};

/**
 * Convert JavaScript value to string for API
 * @param {string} key - Setting key
 * @param {any} value - Value to stringify
 * @returns {string} String value for API
 */
const stringifySettingValue = (key, value) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'boolean') {
    return String(value);
  }

  if (typeof value === 'number') {
    return value.toString();
  }

  return String(value);
};

/**
 * Determine if a key should be nested based on naming convention
 * @param {string} key - Setting key
 * @returns {boolean} True if should create nested structure
 */
const shouldNestKey = (key) => {
  // Keys like "store_address_line1", "business_hours_monday_open"
  const nestingPrefixes = [
    'store_address',
    'business_hours',
    'social_media',
    'contact_form',
    'additional_contacts'
  ];

  return nestingPrefixes.some(prefix => key.startsWith(prefix));
};

/**
 * Set nested value in object using underscore-separated path
 * @param {Object} obj - Object to modify
 * @param {string} path - Underscore-separated path
 * @param {any} value - Value to set
 *
 * @example
 * const obj = {};
 * setNestedValue(obj, "store_address_line1", "123 Main St");
 * // Result: { store_address: { line1: "123 Main St" } }
 */
const setNestedValue = (obj, path, value) => {
  // Convert "store_address_line1" → ["store", "address", "line1"]
  const parts = path.split('_');

  // Find the nesting prefix
  let prefixLength = 1;
  const nestingPrefixes = ['store_address', 'business_hours', 'social_media', 'contact_form', 'additional_contacts'];

  for (const prefix of nestingPrefixes) {
    if (path.startsWith(prefix)) {
      prefixLength = prefix.split('_').length;
      break;
    }
  }

  // Build nested structure
  const parentKeys = parts.slice(0, prefixLength);
  const childKeys = parts.slice(prefixLength);

  let current = obj;

  // Create parent object
  for (const key of parentKeys) {
    if (!current[key]) {
      current[key] = {};
    }
    current = current[key];
  }

  // Set child value
  if (childKeys.length === 0) {
    // Edge case: no child keys
    Object.assign(obj, { [parts.join('_')]: value });
  } else {
    const finalKey = childKeys.join('_');
    current[finalKey] = value;
  }
};

/**
 * Flatten nested object into array of {key, value} pairs
 * @param {Object} obj - Object to flatten
 * @param {string} prefix - Prefix for keys
 * @returns {Array} Array of {key, value} objects
 *
 * @example
 * const nested = { address: { line1: "123 Main", city: "London" } };
 * const flat = flattenObject(nested, "store");
 * // Result: [
 * //   { key: "store_address_line1", value: "123 Main" },
 * //   { key: "store_address_city", value: "London" }
 * // ]
 */
const flattenObject = (obj, prefix = '') => {
  const result = [];

  for (const [key, value] of Object.entries(obj)) {
    const fullKey = prefix ? `${prefix}_${key}` : key;

    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      // Recursively flatten nested objects
      result.push(...flattenObject(value, fullKey));
    } else {
      result.push({ key: fullKey, value });
    }
  }

  return result;
};

/**
 * Validate settings object structure
 * @param {Object} settings - Settings to validate
 * @returns {Object} Validation result { valid: boolean, errors: Array }
 */
export const validateSettingsStructure = (settings) => {
  const errors = [];

  if (!settings || typeof settings !== 'object') {
    return {
      valid: false,
      errors: ['Settings must be an object']
    };
  }

  for (const [section, fields] of Object.entries(settings)) {
    if (typeof fields !== 'object') {
      errors.push(`Section "${section}" must be an object`);
      continue;
    }

    for (const [key, value] of Object.entries(fields)) {
      // Check for invalid values
      if (value === undefined) {
        errors.push(`Setting "${section}.${key}" has undefined value`);
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Deep clone settings object
 * @param {Object} settings - Settings to clone
 * @returns {Object} Cloned settings
 */
export const cloneSettings = (settings) => {
  if (!settings) return {};

  try {
    return JSON.parse(JSON.stringify(settings));
  } catch (error) {
    console.error('[Transform] Failed to clone settings:', error);
    return {};
  }
};

/**
 * Merge settings objects (useful for partial updates)
 * @param {Object} base - Base settings
 * @param {Object} updates - Settings to merge in
 * @returns {Object} Merged settings
 */
export const mergeSettings = (base, updates) => {
  const result = cloneSettings(base);

  for (const [section, fields] of Object.entries(updates)) {
    if (!result[section]) {
      result[section] = {};
    }

    Object.assign(result[section], fields);
  }

  return result;
};

// Export all functions for testing
export default {
  transformNewToOld,
  transformOldToNew,
  parseSettingValue,
  stringifySettingValue,
  shouldNestKey,
  setNestedValue,
  flattenObject,
  validateSettingsStructure,
  cloneSettings,
  mergeSettings
};
